import { balance, type Balance } from './balance';
import { effectiveStat, resolveMatchup } from './matchup';
import { DEFENSE_SLOTS, OFFENSE_SLOTS } from './playdef';
import type { Rng } from './rng';
import type { Combatant, DefenseSlot, KickResult, OffenseSlot } from './types';

/**
 * Punts, field goals, and extra points. Sp. Atk is the leg (range, accuracy, distance); the
 * defense's strongest Atk rushes the kick in a `kick` matchup, where a rusher crit blocks it and a
 * rusher win is pressure. No HP changes hands on a kick. Every number is in balance.json `kicking`.
 */

/** The offense's highest Sp. Atk kicks; slot order breaks ties. */
export function kickerSlot(lineup: Record<OffenseSlot, Combatant>, b: Balance): OffenseSlot {
  return best(OFFENSE_SLOTS, (s) => effectiveStat(lineup[s], 'spa', b));
}

/** The defense's highest Atk rushes the kick; slot order breaks ties. */
export function rusherSlot(lineup: Record<DefenseSlot, Combatant>, b: Balance): DefenseSlot {
  return best(DEFENSE_SLOTS, (s) => effectiveStat(lineup[s], 'atk', b));
}

function best<S extends string>(slots: readonly S[], score: (s: S) => number): S {
  let top = slots[0] as S;
  for (const s of slots) if (score(s) > score(top)) top = s;
  return top;
}

/** A field goal's length: yards to the goal line plus the snap and hold. */
export function fieldGoalDistance(ballOn: number, b: Balance = balance): number {
  return 100 - ballOn + b.kicking.snapYards;
}

/** The longest field goal this Sp. Atk can make. */
export function fieldGoalRange(spa: number, b: Balance): number {
  const k = b.kicking;
  return k.fieldGoal.rangeYards + k.fieldGoal.rangePerSpa * (spa - k.spaReference);
}

/** The chance a kick of `distance` yards is good, before the roll; 0 past the kicker's range. */
export function fieldGoalOdds(
  spa: number,
  distance: number,
  pressured: boolean,
  b: Balance,
): number {
  if (distance > fieldGoalRange(spa, b)) return 0;
  const fg = b.kicking.fieldGoal;
  const p =
    fg.baseAccuracy -
    fg.dropPerYard * Math.max(0, distance - fg.freeYards) +
    fg.spaWeight * (spa / b.kicking.spaReference - 1) +
    (pressured ? fg.pressure : 0);
  return Math.min(fg.maxAccuracy, Math.max(fg.minAccuracy, p));
}

function rush(
  offense: Record<OffenseSlot, Combatant>,
  defense: Record<DefenseSlot, Combatant>,
  rng: Rng,
  b: Balance,
): KickResult & { spa: number } {
  const k = kickerSlot(offense, b);
  const r = rusherSlot(defense, b);
  const result = resolveMatchup({ kind: 'kick', a: offense[k], b: defense[r] }, rng, b);
  const blocked = result.crit === 'b';
  return {
    kicker: `off:${k}`,
    rusher: `def:${r}`,
    rush: result,
    blocked,
    pressured: !blocked && result.winner === 'b',
    spa: effectiveStat(offense[k], 'spa', b),
  };
}

export interface FieldGoalResult extends KickResult {
  distance: number;
  range: number;
  accuracy: number;
  /** The accuracy roll; 1 when the kick was blocked and never rolled. */
  roll: number;
  good: boolean;
}

/** A field goal (or extra point) from `ballOn`. Draws the rush, then one accuracy roll unless blocked. */
export function resolveFieldGoal(
  offense: Record<OffenseSlot, Combatant>,
  defense: Record<DefenseSlot, Combatant>,
  ballOn: number,
  rng: Rng,
  b: Balance,
): FieldGoalResult {
  const { spa, ...kick } = rush(offense, defense, rng, b);
  const distance = fieldGoalDistance(ballOn, b);
  const range = fieldGoalRange(spa, b);
  const accuracy = kick.blocked ? 0 : fieldGoalOdds(spa, distance, kick.pressured, b);
  const roll = kick.blocked ? 1 : rng.next();
  return { ...kick, distance, range, accuracy, roll, good: roll < accuracy };
}

export interface PuntResult extends KickResult {
  /** How far the ball flew past the line of scrimmage; 0 when blocked. */
  yards: number;
  /** It came down in or past the end zone. */
  touchback: boolean;
}

/** A punt from `ballOn`. Draws the rush, then one distance roll unless blocked. */
export function resolvePunt(
  offense: Record<OffenseSlot, Combatant>,
  defense: Record<DefenseSlot, Combatant>,
  ballOn: number,
  rng: Rng,
  b: Balance,
): PuntResult {
  const { spa, ...kick } = rush(offense, defense, rng, b);
  if (kick.blocked) return { ...kick, yards: 0, touchback: false };
  const p = b.kicking.punt;
  const flight =
    (p.yards + p.yardsPerSpa * (spa - b.kicking.spaReference)) * rng.roll(b.rolls.min, b.rolls.max);
  const yards = Math.max(0, Math.round(flight - (kick.pressured ? p.pressureYards : 0)));
  return { ...kick, yards, touchback: ballOn + yards >= 100 };
}
